// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Stack } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';

interface DashboardAlertsProps {
  unreadNotesCount: number;
  overduePaymentsCount: number;
}

/**
 * Renders the two red banners that anchor the patient dashboard. Each banner
 * is dismissable per session (state is local; refresh restores it). A banner
 * is hidden entirely when its underlying count is zero.
 */
export function DashboardAlerts({ unreadNotesCount, overduePaymentsCount }: DashboardAlertsProps): JSX.Element | null {
  const [notesDismissed, setNotesDismissed] = useState(false);
  const [paymentsDismissed, setPaymentsDismissed] = useState(false);

  const showNotes = !notesDismissed && unreadNotesCount > 0;
  const showPayments = !paymentsDismissed && overduePaymentsCount > 0;

  if (!showNotes && !showPayments) {
    return null;
  }

  return (
    <Stack gap="sm">
      {showNotes && (
        <Alert
          color="red"
          icon={<IconAlertCircle />}
          title="Outstanding Notes"
          withCloseButton
          onClose={() => setNotesDismissed(true)}
        >
          You have <strong>{unreadNotesCount}</strong> {unreadNotesCount === 1 ? 'unread note' : 'unread notes'} from
          your care team.
        </Alert>
      )}
      {showPayments && (
        <Alert
          color="red"
          icon={<IconAlertCircle />}
          title="Outstanding Payments"
          withCloseButton
          onClose={() => setPaymentsDismissed(true)}
        >
          You have <strong>{overduePaymentsCount}</strong> outstanding{' '}
          {overduePaymentsCount === 1 ? 'payment' : 'payments'}.
        </Alert>
      )}
    </Stack>
  );
}
